import { useState } from 'react';
import axios from 'axios';
import { FaFileUpload, FaSearch, FaChartLine } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/IndicadoresPreviredPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página de Indicadores Previred
 * Carga y visualización de indicadores previsionales mensuales
 */
const IndicadoresPreviredPage = ({ token }) => {
    const currentDate = new Date();
    const [mes, setMes] = useState(currentDate.getMonth() + 1);
    const [anio, setAnio] = useState(currentDate.getFullYear());
    const [pdfFile, setPdfFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [indicadores, setIndicadores] = useState(null);
    const [searched, setSearched] = useState(false);
    // Estados para guardar el mes/año de los datos actualmente mostrados
    const [mesActual, setMesActual] = useState(null);
    const [anioActual, setAnioActual] = useState(null);

    const meses = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const handleBuscar = async () => {
        try {
            setLoading(true);
            setSearched(true);

            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/indicadores-previred`,
                {
                    params: { mes, anio },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setIndicadores(response.data.data);
                // Guardar el mes y año de los datos que se están mostrando
                setMesActual(mes);
                setAnioActual(anio);
            }
        } catch (error) {
            console.error('Error al buscar indicadores:', error);
            Swal.fire('Error', 'No se pudieron cargar los indicadores', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        } else {
            Swal.fire('Error', 'Solo se permiten archivos PDF', 'error');
            e.target.value = null;
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!pdfFile) {
            Swal.fire('Error', 'Debe seleccionar un archivo PDF', 'error');
            return;
        }

        try {
            setUploading(true);

            const formData = new FormData();
            formData.append('pdf', pdfFile);
            formData.append('mes', mes);
            formData.append('anio', anio);

            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/indicadores-previred/procesar`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                Swal.fire('Éxito', response.data.message, 'success');
                setPdfFile(null);
                document.getElementById('pdfInput').value = null;

                // Buscar los indicadores recién cargados
                handleBuscar();
            }
        } catch (error) {
            console.error('Error al procesar PDF:', error);

            // Manejar error de conflicto (409) - ya existen datos para este período
            if (error.response?.status === 409) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Indicadores ya existen',
                    text: error.response.data.message,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#ffd700'
                });
            } else {
                Swal.fire(
                    'Error',
                    error.response?.data?.message || 'Error al procesar el PDF',
                    'error'
                );
            }
        } finally {
            setUploading(false);
        }
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined) return '-';
        return parseFloat(value).toLocaleString('es-CL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="indicadores-container">
            <div className="indicadores-header">
                <div>
                    <h1><FaChartLine /> Indicadores Previred</h1>
                    <p>Carga y consulta de indicadores previsionales mensuales</p>
                </div>
            </div>

            {/* Formulario de Búsqueda */}
            <div className="search-section">
                <h2><FaSearch /> Buscar Indicadores</h2>
                <div className="search-form">
                    <div className="form-group">
                        <label>Mes</label>
                        <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
                            {meses.map((m, index) => (
                                <option key={index} value={index + 1}>{m}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Año</label>
                        <select value={anio} onChange={(e) => setAnio(parseInt(e.target.value))}>
                            {[2023, 2024, 2025, 2026].map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                    <button className="btn-search" onClick={handleBuscar} disabled={loading}>
                        {loading ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>
            </div>

            {/* Formulario de Carga */}
            <div className="upload-section">
                <h2><FaFileUpload /> Cargar PDF de Indicadores</h2>
                <form onSubmit={handleUpload} className="upload-form">
                    <div className="file-input-group">
                        <input
                            id="pdfInput"
                            type="file"
                            accept="application/pdf"
                            onChange={handleFileChange}
                            disabled={uploading}
                        />
                        {pdfFile && <span className="file-name">{pdfFile.name}</span>}
                    </div>
                    <button
                        type="submit"
                        className="btn-upload"
                        disabled={uploading || !pdfFile}
                    >
                        {uploading ? 'Procesando...' : 'Procesar PDF'}
                    </button>
                </form>
                {uploading && (
                    <p className="processing-message">
                        Procesando PDF con Claude AI... Esto puede tomar unos momentos.
                    </p>
                )}
            </div>

            {/* Resultados */}
            {searched && !loading && (
                <>
                    {!indicadores ? (
                        <div className="no-data">
                            <p>No se encontraron indicadores para {meses[mesActual - 1]} {anioActual}</p>
                            <p>Por favor, cargue el PDF correspondiente a este período</p>
                        </div>
                    ) : (
                        <div className="indicadores-content">
                            <h2>Indicadores de {meses[mesActual - 1]} {anioActual}</h2>

                            {/* Valores Económicos */}
                            <div className="indicadores-section">
                                <h3>Valores Económicos</h3>
                                <table className="indicadores-table">
                                    <thead>
                                        <tr>
                                            <th>Indicador</th>
                                            <th>Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>UF Fin de Mes</td>
                                            <td>${formatNumber(indicadores.uf_fin_mes)}</td>
                                        </tr>
                                        <tr>
                                            <td>UF Mes Anterior</td>
                                            <td>${formatNumber(indicadores.uf_mes_anterior)}</td>
                                        </tr>
                                        <tr>
                                            <td>UTM</td>
                                            <td>${formatNumber(indicadores.utm)}</td>
                                        </tr>
                                        <tr>
                                            <td>UTA</td>
                                            <td>${formatNumber(indicadores.uta)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Rentas Tope Imponibles */}
                            <div className="indicadores-section">
                                <h3>Rentas Tope Imponibles</h3>
                                <table className="indicadores-table">
                                    <thead>
                                        <tr>
                                            <th>Concepto</th>
                                            <th>Pesos</th>
                                            <th>UF</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>AFP</td>
                                            <td>${formatNumber(indicadores.renta_tope_afp_pesos)}</td>
                                            <td>{formatNumber(indicadores.renta_tope_afp_uf)} UF</td>
                                        </tr>
                                        <tr>
                                            <td>INP</td>
                                            <td>${formatNumber(indicadores.renta_tope_inp_pesos)}</td>
                                            <td>{formatNumber(indicadores.renta_tope_inp_uf)} UF</td>
                                        </tr>
                                        <tr>
                                            <td>Seguro Cesantía</td>
                                            <td>${formatNumber(indicadores.renta_tope_seguro_cesantia_pesos)}</td>
                                            <td>{formatNumber(indicadores.renta_tope_seguro_cesantia_uf)} UF</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Rentas Mínimas */}
                            <div className="indicadores-section">
                                <h3>Rentas Mínimas Imponibles</h3>
                                <table className="indicadores-table">
                                    <thead>
                                        <tr>
                                            <th>Categoría</th>
                                            <th>Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Trabajadores Dependientes</td>
                                            <td>${formatNumber(indicadores.renta_minima_dependientes)}</td>
                                        </tr>
                                        <tr>
                                            <td>Menores 18 y Mayores 65</td>
                                            <td>${formatNumber(indicadores.renta_minima_menores18_mayores65)}</td>
                                        </tr>
                                        <tr>
                                            <td>Casa Particular</td>
                                            <td>${formatNumber(indicadores.renta_minima_casa_particular)}</td>
                                        </tr>
                                        <tr>
                                            <td>No Remuneracional</td>
                                            <td>${formatNumber(indicadores.renta_minima_no_remuneracional)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Tasas AFP */}
                            <div className="indicadores-section">
                                <h3>Tasas AFP - Cotizaciones (%)</h3>
                                <table className="indicadores-table wide">
                                    <thead>
                                        <tr>
                                            <th>AFP</th>
                                            <th>Trabajador</th>
                                            <th>Empleador</th>
                                            <th>Total</th>
                                            <th>Total + SIS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Capital</td>
                                            <td>{formatNumber(indicadores.afp_capital_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_capital_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_capital_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_capital_total_sis)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Cuprum</td>
                                            <td>{formatNumber(indicadores.afp_cuprum_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_cuprum_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_cuprum_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_cuprum_total_sis)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Habitat</td>
                                            <td>{formatNumber(indicadores.afp_habitat_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_habitat_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_habitat_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_habitat_total_sis)}%</td>
                                        </tr>
                                        <tr>
                                            <td>PlanVital</td>
                                            <td>{formatNumber(indicadores.afp_planvital_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_planvital_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_planvital_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_planvital_total_sis)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Provida</td>
                                            <td>{formatNumber(indicadores.afp_provida_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_provida_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_provida_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_provida_total_sis)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Modelo</td>
                                            <td>{formatNumber(indicadores.afp_modelo_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_modelo_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_modelo_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_modelo_total_sis)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Uno</td>
                                            <td>{formatNumber(indicadores.afp_uno_dependiente_trabajador)}%</td>
                                            <td>{formatNumber(indicadores.afp_uno_dependiente_empleador)}%</td>
                                            <td>{formatNumber(indicadores.afp_uno_total)}%</td>
                                            <td>{formatNumber(indicadores.afp_uno_total_sis)}%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Seguro de Cesantía */}
                            <div className="indicadores-section">
                                <h3>Seguro de Cesantía (%)</h3>
                                <table className="indicadores-table">
                                    <thead>
                                        <tr>
                                            <th>Tipo Contrato</th>
                                            <th>Empleador</th>
                                            <th>Trabajador</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Indefinido</td>
                                            <td>{formatNumber(indicadores.cesantia_indefinido_empleador)}%</td>
                                            <td>{formatNumber(indicadores.cesantia_indefinido_trabajador)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Plazo Fijo</td>
                                            <td>{formatNumber(indicadores.cesantia_fijo_empleador)}%</td>
                                            <td>{formatNumber(indicadores.cesantia_fijo_trabajador)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Indefinido 11+ años</td>
                                            <td>{formatNumber(indicadores.cesantia_indefinido_11mas_empleador)}%</td>
                                            <td>{formatNumber(indicadores.cesantia_indefinido_11mas_trabajador)}%</td>
                                        </tr>
                                        <tr>
                                            <td>Casa Particular</td>
                                            <td>{formatNumber(indicadores.cesantia_casa_particular_empleador)}%</td>
                                            <td>{formatNumber(indicadores.cesantia_casa_particular_trabajador)}%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Asignación Familiar */}
                            <div className="indicadores-section">
                                <h3>Asignación Familiar</h3>
                                <table className="indicadores-table">
                                    <thead>
                                        <tr>
                                            <th>Tramo</th>
                                            <th>Monto</th>
                                            <th>Desde</th>
                                            <th>Hasta</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>A</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_a_monto)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_a_desde)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_a_hasta)}</td>
                                        </tr>
                                        <tr>
                                            <td>B</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_b_monto)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_b_desde)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_b_hasta)}</td>
                                        </tr>
                                        <tr>
                                            <td>C</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_c_monto)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_c_desde)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_c_hasta)}</td>
                                        </tr>
                                        <tr>
                                            <td>D</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_d_monto)}</td>
                                            <td>${formatNumber(indicadores.asig_familiar_tramo_d_desde)}</td>
                                            <td>Sin tope</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default IndicadoresPreviredPage;
